import React from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Link,
    Button,
    Tooltip,
    makeStyles
} from '@material-ui/core';
import { CheckCircle, OpenInNew, Dashboard, Code } from '@material-ui/icons';
import { useStateValue } from '../context/state/provider';
import { WIZARD_STEPS } from '../constants/wizardSteps.constants';
import { VIEW } from '../constants/view.constants';

const useStyles = makeStyles((theme) => ({
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafe',
        padding: theme.spacing(2)
    },
    card: {
        maxWidth: 600,
        padding: theme.spacing(4),
        textAlign: 'center',
        borderRadius: theme.spacing(2)
    },
    successIcon: {
        fontSize: 80,
        color: theme.palette.success.main,
        marginBottom: theme.spacing(2)
    },
    title: {
        marginBottom: theme.spacing(2),
        color: theme.palette.success.main
    },
    description: {
        marginBottom: theme.spacing(3),
        color: theme.palette.text.secondary
    },
    nextSteps: {
        textAlign: 'left',
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius
    },

    agentLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.shape.borderRadius,
        textDecoration: 'none',
        color: 'white',
        fontWeight: 'bold',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            color: 'white'
        }
    },
    footer: {
        textAlign: 'center',
        marginTop: theme.spacing(4),
        paddingTop: theme.spacing(2),
        borderTop: '1px solid #e0e0e0',
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(2),
            paddingTop: theme.spacing(1.5)
        }
    },
    customTooltip: {
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.common.white,
        fontSize: '12px',
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(1),
        maxWidth: 200
    },
    tooltipArrow: {
        display: 'none'
    },
    footerLogo: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(0.5),
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.25rem'
        }
    },
    footerSubtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem'
    },
    accountCreatedBanner: {
        backgroundColor: '#d4edda',
        border: '1px solid #c3e6cb',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(3),
        color: '#155724',
        textAlign: 'left'
    },
    accountCreatedText: {
        fontSize: '0.95rem',
        lineHeight: 1.6,
        margin: 0
    },
    bottomSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginTop: theme.spacing(3),
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(2),
            alignItems: 'stretch'
        }
    },
    helpSection: {
        display: 'flex',
        justifyContent: 'flex-start',
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'center'
        }
    },
    helpButton: {
        padding: theme.spacing(0.75, 1),
        backgroundColor: '#ffffff',
        color: theme.palette.primary.main,
        border: `2px solid ${theme.palette.primary.main}`,
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        maxWidth: '230px',
        minHeight: '35px',
        fontSize: '14px',
        margin: '0 auto',
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.main
        },
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            width: '100%'
        }
    },
    actionButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        textDecoration: 'none',
        borderRadius: theme.shape.borderRadius,
        fontSize: '0.875rem',
        fontWeight: 'bold',
        minWidth: '220px',
        width: '220px',
        minHeight: '48px',
        height: '48px',
        textAlign: 'center',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            color: 'white',
            textDecoration: 'none'
        }
    },
    centeredButtonContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3)
    },
    sectionText: {
        textAlign: 'center',
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
        fontWeight: 500
    }
}));

export default function SuccessView() {
    const classes = useStyles();
    const [{ wizardData }, dispatch] = useStateValue();

    const handleNeedHelp = () => {
        dispatch({
            type: 'SET_PREVIOUS_STEP',
            step: VIEW.SUCCESS
        });
        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.WHITE_GLOVE_SERVICE
        });
        dispatch({
            type: 'SET_VIEW',
            view: VIEW.WIZARD
        });
    };

    // Extract publish response data
    const publishResponse = wizardData.publishResponse;
    // console.log('🔍 DEBUG: wizardData:', wizardData);
    // console.log('🔍 DEBUG: publishResponse:', publishResponse);
    
    // The actual agent data is nested inside publishResponse.data
    const agentData = publishResponse?.data;
    const isLive = agentData?.isLive !== false; // Default to true for backward compatibility
    const agentStatus = agentData?.agentStatus || 'online';
    const limitInfo = agentData?.limitInfo;
    const isDraft = agentStatus === 'draft' || !isLive;
    const onboardingWarning = agentData?.onboardingWarning;
    
    // console.log('🔍 DEBUG: agentData:', agentData);
    // console.log('🔍 DEBUG: isLive:', isLive);
    // console.log('🔍 DEBUG: agentStatus:', agentStatus);
    // console.log('🔍 DEBUG: isDraft:', isDraft);
    // console.log('🔍 DEBUG: limitInfo:', limitInfo);



    // Generate agent URLs
    // const chatbotAppUrl = process.env.REACT_APP_CHATBOT_URL;
    const chatbotAppUrl = process.env.REACT_APP_CHATBOT_URL_SESSION;
    const adminPanelBaseUrl = process.env.REACT_APP_ADMIN_PANEL_URL;
    const demo1Url = process.env.REACT_APP_DEMO1_URL;
    const agentUrl = `${chatbotAppUrl}&id=${wizardData.chatbotId}`;

    return (
        <div className={classes.container}>
            <Card className={classes.card}>
                <CardContent>
                    <CheckCircle className={classes.successIcon} />
                    
                    <Typography variant="h4" className={classes.title}>
                        Agent Created Successfully!
                    </Typography>
                    
                    <Typography variant="body1" className={classes.description}>
                        Your agent "<strong>{wizardData.chatbotName}</strong>" has been successfully published{isDraft ? ' and is currently in draft mode' : ' and is now live'}!
                    </Typography>


                    {/* Show limit warning if agent is in draft mode */}
                    {isDraft && limitInfo && (
                        <Box style={{ 
                            margin: '16px 0', 
                            padding: '16px', 
                            backgroundColor: '#fff3cd', 
                            border: '1px solid #ffeaa7', 
                            borderRadius: '8px',
                            textAlign: 'left'
                        }}>
                            <Typography variant="body2" style={{ color: '#856404', marginBottom: '12px' }}>
                                <strong>⚠️ Agent Status: Draft Mode</strong>
                            </Typography>
                            <Typography variant="body2" style={{ color: '#856404', marginBottom: '12px' }}>
                                You have reached your live agent limit ({limitInfo.maxLiveAgents}). 
                                Your agent has been created successfully but is in draft mode.
                            </Typography>
                            <Typography variant="body2" style={{ color: '#856404', marginBottom: '16px' }}>
                                To make it live, you can either:
                            </Typography>
                            <Box style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <Link
                                    href={`${process.env.REACT_APP_ADMIN_PANEL_URL}/sail/admin/agents`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '8px 12px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        textDecoration: 'none',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <Dashboard fontSize="small" />
                                    Manage Agents
                                </Link>
                                <Link
                                    href={limitInfo.contactUrl || 'https://sail.cx/contact/'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '8px 12px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        textDecoration: 'none',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <OpenInNew fontSize="small" />
                                    {limitInfo.upgradeAction?.buttonText || 'Upgrade Plan'}
                                </Link>
                            </Box>
                        </Box>
                    )}

                    {/* Show onboarding warning if there was a subscription setup issue */}
                    {onboardingWarning && (
                        <Box style={{ 
                            margin: '16px 0', 
                            padding: '16px', 
                            backgroundColor: '#fff3cd', 
                            border: '1px solid #ffeaa7', 
                            borderRadius: '8px',
                            textAlign: 'left'
                        }}>
                            <Typography variant="body2" style={{ color: '#856404', marginBottom: '12px' }}>
                                <strong>⚠️ Subscription Setup Notice</strong>
                            </Typography>
                            <Typography variant="body2" style={{ color: '#856404' }}>
                                {onboardingWarning.message}
                            </Typography>
                        </Box>
                    )}

                    <Typography variant="body1" className={classes.sectionText}>
                        Try out your Q&A Agent and experience it as your user would. Click on one of the buttons below to open the agent in a new tab.
                    </Typography>
                    
                    <Box className={classes.centeredButtonContainer}>
                        <Link
                            href={agentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.actionButton}
                        >
                            <OpenInNew fontSize="small" />
                            Full Page Web View
                        </Link>
                        
                        <Link
                            href={`${demo1Url}/?id=${wizardData.chatbotId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.actionButton}
                        >
                            <Code fontSize="small" />
                            Embedded Chat View
                        </Link>
                    </Box>

                    {(wizardData.prefilledFromAdmin || wizardData.wizardToken || wizardData.isExistingClient) && (
                        <>
                            {wizardData.prefilledFromAdmin ? (
                                <Typography variant="body1" className={classes.sectionText}>
                                    To make fine tuning adjustments to your new agent, you can return to the Agent Admin Panel where you started this wizard.
                                </Typography>
                            ) : (
                                <>
                                    <Typography variant="body1" className={classes.sectionText}>
                                        To access the Agent Admin Panel and make fine tuning adjustment, click on the button below.
                                    </Typography>
                                    
                                    <Box className={classes.centeredButtonContainer}>
                                        <Link
                                            href={`${adminPanelBaseUrl}/sail/admin/agents`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={classes.actionButton}
                                        >
                                            <Dashboard fontSize="small" />
                                            Agent Admin Panel
                                        </Link>
                                    </Box>
                                </>
                            )}
                        </>
                    )}

                    {/* Commented out Agent Details section */}
                    {/*
                    <Box className={classes.nextSteps}>
                        <Typography variant="h6" gutterBottom>
                            🎉 Your Agent is Live!
                        </Typography>
                        <Typography variant="body2" component="div" style={{ paddingLeft: 20 }}>
                            <p><strong>Agent Details:</strong></p>
                            <ul>
                                <li><strong>Name:</strong> {wizardData.chatbotName}</li>
                                <li><strong>Topic:</strong> {wizardData.skillDescription}</li>
                                <li><strong>Documents:</strong> {wizardData.files.length} files processed</li>
                                <li><strong>Owner:</strong> {wizardData.userDetails.firstName} {wizardData.userDetails.lastName}</li>
                                <li><strong>Company:</strong> {wizardData.userDetails.company}</li>
                            </ul>
                        </Typography>
                    </Box>
                    */}

                    {!wizardData.prefilledFromAdmin && !wizardData.isExistingClient && (
                        <>
                            <Box className={classes.accountCreatedBanner}>
                                <Typography className={classes.accountCreatedText}>
                                    Your account is being created. <strong>It will take approximately 5 minutes.</strong> Please check your email for your credentials. <span style={{ fontSize: '0.85em' }}>(If you do not see the email in 5 minutes, please check your spam filter).</span>
                                </Typography>
                            </Box>
                            
                            <Box className={classes.centeredButtonContainer}>
                                <Link
                                    href={`${adminPanelBaseUrl}/sail/admin/agents`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={classes.actionButton}
                                >
                                    <Dashboard fontSize="small" />
                                    Agent Admin Panel
                                </Link>
                            </Box>
                        </>
                    )}

                    <Box className={classes.bottomSection}>
                        <Box className={classes.helpSection}>
                            <Tooltip 
                                title="Click here if you need support"
                                classes={{ tooltip: classes.customTooltip, arrow: classes.tooltipArrow }}
                                placement="top"
                            >
                                <Button
                                    className={classes.helpButton}
                                    onClick={handleNeedHelp}
                                    variant="outlined"
                                >
                                    Need help?
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box className={classes.footer}>
                        <Typography variant="h6" className={classes.footerLogo}>
                            Sail.cx<sup style={{fontSize: '0.6em'}}>®</sup>
                        </Typography>
                        <Typography variant="body2" className={classes.footerSubtitle}>
                            By PremiseHQ
                        </Typography>
                    </Box>

                </CardContent>
            </Card>
        </div>
    );
}
